import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Badge, Button, Card, Form, InputGroup, Modal, Spinner, Table} from "react-bootstrap";
import {toast} from "react-toastify";
import {
    fetchDashboardPromotionProofsAllApi,
    moderateDashboardPromotionProofApi,
} from "../../http/userAPI";
import ImageLightbox from "../common/ImageLightbox";

const PAGE_SIZE = 20;

const fmtDate = (value) =>
    value
        ? new Date(value)
            .toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            })
            .replace(",", "")
        : "-";

const statusOptions = [
    {value: "all", label: "All"},
    {value: "pending", label: "Pending"},
    {value: "approved", label: "Approved"},
    {value: "rejected", label: "Rejected"},
];

const statusClass = (status) => {
    if (status === "approved") return "text-success";
    if (status === "rejected") return "text-danger";
    if (status === "pending") return "text-warning";
    return "text-secondary";
};

const proofImageUrl = (screenshotPath) => {
    if (!screenshotPath) return "";
    const base = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    return `${base}/promo_bounty/${screenshotPath}`;
};

const AdminDashboardPromotionProofs = ({promotion}) => {
    const promotionKey = useMemo(() => String(promotion?.promotion_key || "").trim(), [promotion]);
    const [status, setStatus] = useState("pending");
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [fullImage, setFullImage] = useState(null);

    const pages = useMemo(() => Math.max(1, Math.ceil((total || 0) / PAGE_SIZE)), [total]);

    const load = useCallback(async () => {
        if (!promotionKey) {
            setRows([]);
            setTotal(0);
            return;
        }

        setLoading(true);
        try {
            const data = await fetchDashboardPromotionProofsAllApi({
                promotionKey,
                status: status === "all" ? undefined : status,
                q: query,
                page,
                limit: PAGE_SIZE,
            });
            const list = data?.requests || [];
            setRows(list);
            setTotal(Number.isFinite(data?.total) ? data.total : list.length);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Failed to load promotion applications", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
        } finally {
            setLoading(false);
        }
    }, [page, promotionKey, query, status]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        setPage(1);
    }, [status, promotionKey]);

    const onSearch = (event) => {
        event?.preventDefault?.();
        setPage(1);
        load();
    };

    const moderate = async (nextStatus) => {
        if (!editing) return;
        setSaving(true);
        try {
            await moderateDashboardPromotionProofApi({
                id: editing.id,
                status: nextStatus,
                moderator_note: editing.moderator_note || "",
            });
            toast.success(nextStatus === "approved" ? "Application approved" : nextStatus === "rejected" ? "Application rejected" : "Application moved to pending", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Moderation failed", {
                position: "bottom-center",
                autoClose: 3000,
                hideProgressBar: false,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="shadow-sm mt-3">
            <ImageLightbox
                src={fullImage}
                alt="Promotion proof full image"
                onClose={() => setFullImage(null)}
            />
            <Card.Header className="d-flex flex-wrap justify-content-between align-items-end gap-2">
                <div>
                    <span className="fw-semibold text-success">Promotion Applications</span>
                    <div className="small text-muted">Current key: {promotionKey || "-"}</div>
                </div>
                <Form onSubmit={onSearch} className="d-flex gap-2 flex-wrap align-items-end">
                    <Form.Group>
                        <Form.Label className="small text-muted mb-1">Status</Form.Label>
                        <Form.Select size="sm" value={status} onChange={(event) => setStatus(event.target.value)}>
                            {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="small text-muted mb-1">Search</Form.Label>
                        <InputGroup size="sm">
                            <Form.Control
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="user / text / response"
                            />
                            <Button type="submit" variant="secondary">Search</Button>
                        </InputGroup>
                    </Form.Group>
                    <Button variant="outline-primary" size="sm" onClick={load} disabled={loading}>
                        {loading ? <Spinner animation="border" size="sm" /> : "Reload"}
                    </Button>
                </Form>
            </Card.Header>
            <Card.Body>
                <div style={{overflowX: "auto", whiteSpace: "nowrap", paddingBottom: 8}}>
                    {loading ? (
                        <div className="text-muted small">Loading...</div>
                    ) : rows.length === 0 ? (
                        <div className="text-muted small">No applications for this promotion.</div>
                    ) : (
                        <Table bordered hover size="sm" striped style={{minWidth: 1100, lineHeight: 1.2}}>
                            <thead className="table-light text-muted small">
                            <tr>
                                <th className="text-center">ID</th>
                                <th>Date</th>
                                <th>UID</th>
                                <th>Username</th>
                                <th>AMZ</th>
                                <th>Photo</th>
                                <th>Proof Text</th>
                                <th className="text-center">Status</th>
                                <th>Staff Response</th>
                                <th className="text-center">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {rows.map((row) => {
                                const imageUrl = proofImageUrl(row.screenshot_path);
                                return (
                                    <tr key={row.id} className="small">
                                        <td className="text-center align-middle text-muted">{row.id}</td>
                                        <td className="align-middle text-muted">{fmtDate(row.createdAt || row.created_at)}</td>
                                        <td className="align-middle text-muted">{row.user_id}</td>
                                        <td className="align-middle text-muted">{row.user_name || "-"}</td>
                                        <td className="align-middle text-muted">{row.user_amz_name || "-"}</td>
                                        <td className="align-middle">
                                            {imageUrl ? (
                                                <button
                                                    type="button"
                                                    className="border-0 bg-transparent p-0"
                                                    title="Open full image"
                                                    onClick={() => setFullImage(imageUrl)}
                                                >
                                                    <img
                                                        src={imageUrl}
                                                        alt="Promotion proof"
                                                        className="rounded border"
                                                        style={{width: 74, height: 50, objectFit: "cover", cursor: "zoom-in"}}
                                                    />
                                                </button>
                                            ) : "-"}
                                        </td>
                                        <td
                                            className="align-middle text-muted"
                                            style={{maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis"}}
                                            title={row.submission_text || ""}
                                        >
                                            {row.submission_text || "-"}
                                        </td>
                                        <td className="text-center align-middle">
                                            <Badge bg="light" text="dark" className={statusClass(row.status)}>
                                                {String(row.status || "pending").toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td
                                            className="align-middle text-muted"
                                            style={{maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis"}}
                                            title={row.moderator_note || ""}
                                        >
                                            {row.moderator_note || "-"}
                                        </td>
                                        <td className="text-center align-middle">
                                            <Button size="sm" variant="outline-primary" onClick={() => setEditing({...row})}>
                                                Moderate
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </Table>
                    )}
                </div>

                <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="small text-muted">Total: {total}</div>
                    <div className="d-flex gap-2">
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={page <= 1}
                            onClick={() => setPage((value) => Math.max(1, value - 1))}
                        >
                            Prev
                        </Button>
                        <div className="small align-self-center">{page} / {pages}</div>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={page >= pages}
                            onClick={() => setPage((value) => Math.min(pages, value + 1))}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </Card.Body>

            <Modal show={!!editing} onHide={() => setEditing(null)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="h6">Moderate Promotion Application #{editing?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editing && (
                        <div className="small">
                            <div className="mb-2 text-muted">
                                <div><strong>User:</strong> {editing.user_name || "-"} (UID {editing.user_id})</div>
                                <div><strong>AMZ:</strong> {editing.user_amz_name || "-"}</div>
                                <div><strong>Date:</strong> {fmtDate(editing.createdAt || editing.created_at)}</div>
                                <div><strong>Promotion:</strong> {editing.promotion_title || promotion?.detail_title || "-"}</div>
                            </div>

                            {editing.screenshot_path && (
                                <button
                                    type="button"
                                    className="border rounded bg-light p-1 mb-3"
                                    style={{maxWidth: 420, cursor: "zoom-in"}}
                                    onClick={() => setFullImage(proofImageUrl(editing.screenshot_path))}
                                >
                                    <img
                                        src={proofImageUrl(editing.screenshot_path)}
                                        alt="Promotion proof"
                                        className="img-fluid rounded"
                                        style={{maxHeight: 260, objectFit: "contain"}}
                                    />
                                </button>
                            )}

                            <Form.Group className="mb-2">
                                <Form.Label className="small text-muted mb-1">User proof text</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    size="sm"
                                    value={editing.submission_text || ""}
                                    readOnly
                                />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label className="small text-muted mb-1">Staff response / promo code</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    size="sm"
                                    placeholder="Example: Approved. Your -50% forever promo code is SUMMERMOVE50."
                                    value={editing.moderator_note || ""}
                                    onChange={(event) => setEditing((value) => ({
                                        ...value,
                                        moderator_note: event.target.value,
                                    }))}
                                />
                            </Form.Group>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <div className="d-flex gap-2">
                        <Button variant="outline-secondary" size="sm" onClick={() => moderate("pending")} disabled={saving}>
                            Set Pending
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => moderate("rejected")} disabled={saving}>
                            {saving ? <Spinner animation="border" size="sm" /> : "Reject"}
                        </Button>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="secondary" size="sm" onClick={() => setEditing(null)}>
                            Close
                        </Button>
                        <Button variant="success" size="sm" onClick={() => moderate("approved")} disabled={saving}>
                            {saving ? <Spinner animation="border" size="sm" /> : "Approve"}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </Card>
    );
};

export default AdminDashboardPromotionProofs;
